/**
 * Opening the Premium / free-trial modal from anywhere.
 *
 * The design prototype exposes a global `ccPremium()` that any surface can call.
 * Production keeps the modal state in `Navigation` (one instance, above the
 * route content), so callers dispatch this event instead of threading a
 * callback through every composition. Same shape as
 * `utils/appModeNavigation.ts`, which already solves the mirror problem for the
 * mobile tab bar.
 */

import { trackEvent } from './analytics';

export const PREMIUM_MODAL_EVENT = 'checkcoa:open-premium-modal';

export interface PremiumModalRequest {
    /** Pre-select a tier in the modal. Omit to keep the modal's own default. */
    plan?: 'collector' | 'dealer';
    /** Which surface asked, for the paywall headline. */
    reason?: 'quota' | 'fraud' | 'generic';
}

export function requestPremiumModal(detail: PremiumModalRequest = {}): void {
    if (typeof window === 'undefined') return;
    // Every Premium / upgrade control on the site funnels through here, so this is
    // the one place that can measure intent. Before 2026-08-12 nothing about the
    // paywall was tracked at all and its conversion could not be discussed.
    // Pairs with `paywall_view` / `paywall_dismiss` in PaywallModal and the
    // existing begin_checkout -> start_trial -> purchase chain.
    try {
        trackEvent('premium_cta_click', {
            reason: detail.reason || 'generic',
            plan: detail.plan || 'none',
            surface: window.location.pathname,
        });
    } catch {
        // analytics must never block the modal
    }
    window.dispatchEvent(new CustomEvent<PremiumModalRequest>(PREMIUM_MODAL_EVENT, { detail }));
}
